// "Theme" rail panel: palette (recolours everything that uses a palette
// colour), page font, paper, margins, background, and invoice numbering.

import React, { useEffect, useState } from "react";
import { Section, Field, NumberInput, SelectInput, Segmented, BoxInput, TextInput, ColorInput } from "../controls";
import { COLOR_TOKENS, TOKEN_LABELS, FONT_FAMILIES, PAGE_SIZES } from "../designModel";

const HEX = /^#[0-9a-f]{6}$/i;

function PaletteRow({ token, value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <div className="td-palette__row">
      <input type="color" aria-label={`${TOKEN_LABELS[token]} colour`} value={HEX.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toLowerCase())} />
      <span className="td-palette__name">{TOKEN_LABELS[token]}</span>
      <input
        className="td-input td-palette__hex"
        aria-label={`${TOKEN_LABELS[token]} hex`}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (HEX.test(e.target.value.trim())) onChange(e.target.value.trim().toLowerCase());
        }}
      />
    </div>
  );
}

export default function ThemePanel({ page, assets, onPage, numbering, onNumbering }) {
  const m = page.margins;
  const [num, setNum] = useState(numbering || { prefix: "", nextNo: 1, pad: 0 });
  useEffect(() => setNum(numbering || { prefix: "", nextNo: 1, pad: 0 }), [numbering]);
  const example = `${num.prefix || ""}${String(num.nextNo || 1).padStart(num.pad || 0, "0")}`;

  return (
    <div className="td-panel">
      <Section title="Colours">
        <p className="td-hint">Elements set to a palette colour change together when you change it here.</p>
        <div className="td-palette">
          {COLOR_TOKENS.map((tk) => (
            <PaletteRow key={tk} token={tk} value={page.palette[tk]} onChange={(v) => onPage((p) => ({ ...p, palette: { ...p.palette, [tk]: v } }), `pal-${tk}`)} />
          ))}
        </div>
      </Section>
      <Section title="Text">
        <Field label="Font">
          <SelectInput value={page.font.family} options={FONT_FAMILIES.map((f) => ({ value: f, label: f }))} onChange={(v) => onPage((p) => ({ ...p, font: { ...p.font, family: v } }))} />
        </Field>
        <Field label="Size">
          <NumberInput value={page.font.size} min={6} max={18} step={0.5} unit="pt" onChange={(v) => onPage((p) => ({ ...p, font: { ...p.font, size: v } }), "font-size")} />
        </Field>
        <Field label="Colour">
          <ColorInput value={page.font.color} palette={page.palette} allowNone={false} onChange={(v) => onPage((p) => ({ ...p, font: { ...p.font, color: v } }))} />
        </Field>
      </Section>
      <Section title="Paper">
        <Field label="Size">
          <SelectInput value={page.size} options={Object.entries(PAGE_SIZES).map(([k, v]) => ({ value: k, label: v.label }))} onChange={(v) => onPage((p) => ({ ...p, size: v }))} />
        </Field>
        <Field label="Orientation">
          <Segmented
            ariaLabel="Orientation"
            value={page.orientation}
            onChange={(v) => v && onPage((p) => ({ ...p, orientation: v }))}
            options={[
              { value: "portrait", label: "Portrait" },
              { value: "landscape", label: "Landscape" },
            ]}
          />
        </Field>
        <Field label="Margins (mm)">
          <BoxInput
            max={40}
            value={[m.top, m.right, m.bottom, m.left]}
            onChange={(v) => {
              const [top, right, bottom, left] = v || [0, 0, 0, 0];
              onPage((p) => ({ ...p, margins: { top, right, bottom, left } }), "margins");
            }}
          />
        </Field>
        <Field label="Page colour">
          <ColorInput value={page.background?.color} palette={page.palette} onChange={(v) => onPage((p) => ({ ...p, background: { ...(p.background || {}), color: v } }))} />
        </Field>
        <Field label="Background image">
          <SelectInput
            value={page.background?.assetId || ""}
            placeholder="None"
            options={assets.map((a) => ({ value: a.id, label: a.filename || a.id }))}
            onChange={(v) => onPage((p) => ({ ...p, background: { ...(p.background || {}), assetId: v || undefined } }))}
          />
        </Field>
        {page.background?.assetId && (
          <Field label="Background opacity">
            <NumberInput
              value={Math.round((page.background.opacity ?? 1) * 100)}
              min={0}
              max={100}
              step={5}
              unit="%"
              onChange={(v) => onPage((p) => ({ ...p, background: { ...p.background, opacity: v / 100 } }), "bg-op")}
            />
          </Field>
        )}
      </Section>
      <Section title="Invoice numbers">
        <p className="td-hint">Your own invoice numbers. The next invoice you submit gets <strong>{example}</strong>, then it counts up.</p>
        <div className="td-grid2">
          <Field label="Prefix">
            <TextInput value={num.prefix} maxLength={12} placeholder="e.g. GSK " onChange={(v) => setNum((n) => ({ ...n, prefix: v }))} onCommit={() => onNumbering(num)} />
          </Field>
          <Field label="Next number">
            <NumberInput value={num.nextNo} min={1} max={9999999} onChange={(v) => { const next = { ...num, nextNo: v }; setNum(next); onNumbering(next); }} />
          </Field>
        </div>
        <Field label="Zero padding" hint='3 turns 7 into "007".'>
          <NumberInput value={num.pad} min={0} max={8} onChange={(v) => { const next = { ...num, pad: v || 0 }; setNum(next); onNumbering(next); }} />
        </Field>
      </Section>
    </div>
  );
}
